import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Chip,
  IconButton, MenuItem, FormControl, InputLabel, Select, Grid, Alert, Tooltip,
  Stack, Typography, Switch, FormControlLabel, CircularProgress, InputAdornment,
  Divider
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, People as PeopleIcon, Search as SearchIcon,
  LinkOff as LinkOffIcon, Business as BusinessIcon, ReceiptLong as ReceiptIcon,
  PictureAsPdf as PdfIcon, CheckCircle as PaidIcon, Home as AddressIcon,
  MailOutline as MailIcon, Tune as BillingIcon, Shield as LimitsIcon,
  Send as SendIcon
} from '@mui/icons-material';
import {
  collection, onSnapshot, query, orderBy, where, getDocs, limit, startAt, endAt
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import { db, functions, storage } from '../../firebase/config';
import { PORTAL_ACCESS_ROLES } from '../../redux/slices/authSlice';
import PageHeader from '../common/PageHeader';

const fmtMoney = (v) => `${Number(v || 0).toFixed(2).replace('.', ',')} €`;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Sektions-Überschrift im Anlege-/Bearbeiten-Dialog. */
const FormSection = ({ icon, title, hint, first = false }) => (
  <Grid size={{ xs: 12 }} sx={{ mt: first ? 0 : 1.5 }}>
    <Stack direction="row" spacing={1} alignItems="center">
      {icon}
      <Typography variant="subtitle2">{title}</Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">— {hint}</Typography>
      )}
    </Stack>
    <Divider sx={{ mt: 0.5 }} />
  </Grid>
);

const emptyForm = () => ({
  id: null, customerNumber: null, displayName: '', companyName: '', contactPerson: '',
  street: '', zip: '', city: '', country: 'Deutschland',
  vatId: '', billingEmail: '', ccEmails: '',
  mode: 'monthly', priceMode: 'metered', paymentTermsDays: 14, reference: '',
  autoSendEmail: false,
  maxFarePerRideEur: '', monthlyBudgetEur: '', active: true, notes: ''
});

// ── Mitglieder-Verwaltung (verknüpfte App-/Telefonkunden) ──────────────────
const MembersDialog = ({ invoiceCustomer, onClose }) => {
  const [members, setMembers] = useState([]);
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!invoiceCustomer) return undefined;
    const q = query(collection(db, 'users'), where('invoiceCustomerId', '==', invoiceCustomer.id));
    const unsub = onSnapshot(q, (snap) => {
      setMembers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, () => {});
    return unsub;
  }, [invoiceCustomer]);

  const search = async () => {
    const t = term.trim();
    if (!t) { setResults([]); return; }
    setSearching(true);
    setError('');
    try {
      const found = new Map();
      const add = (snap) => snap.forEach((d) => found.set(d.id, { id: d.id, ...d.data() }));
      if (t.includes('@')) {
        add(await getDocs(query(collection(db, 'users'), where('email', '==', t.toLowerCase()), limit(10))));
      }
      add(await getDocs(query(collection(db, 'users'), where('phoneNumber', '==', t), limit(10))));
      add(await getDocs(query(collection(db, 'users'), orderBy('name'), startAt(t), endAt(`${t}`), limit(10))));
      const list = Array.from(found.values()).filter(
        (u) => u.isDriver !== true && !PORTAL_ACCESS_ROLES.includes(u.role)
      );
      setResults(list.slice(0, 15));
    } catch (e) {
      setError(e?.message || 'Suche fehlgeschlagen');
    } finally {
      setSearching(false);
    }
  };

  const link = async (userId) => {
    setBusy(true); setError('');
    try {
      await httpsCallable(functions, 'linkInvoiceCustomerMember')({
        invoiceCustomerId: invoiceCustomer.id, userId
      });
    } catch (e) { setError(e?.message || 'Verknüpfen fehlgeschlagen'); }
    finally { setBusy(false); }
  };

  const unlink = async (userId) => {
    setBusy(true); setError('');
    try {
      await httpsCallable(functions, 'unlinkInvoiceCustomerMember')({ userId });
    } catch (e) { setError(e?.message || 'Entfernen fehlgeschlagen'); }
    finally { setBusy(false); }
  };

  const memberIds = new Set(members.map((m) => m.id));

  return (
    <Dialog open={Boolean(invoiceCustomer)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Verknüpfte Kunden — {invoiceCustomer?.displayName || invoiceCustomer?.companyName}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="subtitle2" sx={{ mt: 1 }}>Aktuelle Mitglieder</Typography>
        {members.length === 0 && (
          <Typography variant="body2" color="text.secondary">Noch keine App-/Telefonkunden verknüpft.</Typography>
        )}
        {members.map((m) => (
          <Stack key={m.id} direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 0.5 }}>
            <Box>
              <Typography variant="body2">{m.name || '—'}{m.isPhoneCustomer ? ' (Telefon)' : ''}</Typography>
              <Typography variant="caption" color="text.secondary">{m.email || m.phoneNumber || m.id}</Typography>
            </Box>
            <Tooltip title="Verknüpfung entfernen">
              <span><IconButton size="small" color="warning" disabled={busy} onClick={() => unlink(m.id)}><LinkOffIcon fontSize="small" /></IconButton></span>
            </Tooltip>
          </Stack>
        ))}

        <Typography variant="subtitle2" sx={{ mt: 3 }}>Kunde hinzufügen (Suche: Name, E-Mail oder Telefon)</Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <TextField
            fullWidth size="small" value={term} placeholder="z. B. Max Mustermann"
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <Button variant="outlined" onClick={search} disabled={searching}>Suchen</Button>
        </Stack>
        {searching && <CircularProgress size={20} sx={{ mt: 2 }} />}
        {results.map((u) => (
          <Stack key={u.id} direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 0.5 }}>
            <Box>
              <Typography variant="body2">{u.name || '—'}{u.isPhoneCustomer ? ' (Telefon)' : ''}</Typography>
              <Typography variant="caption" color="text.secondary">{u.email || u.phoneNumber || u.id}</Typography>
            </Box>
            {memberIds.has(u.id) ? (
              <Chip size="small" label="verknüpft" color="success" />
            ) : u.invoiceCustomerId ? (
              <Tooltip title="Bereits einem anderen Rechnungskunden zugeordnet — Verknüpfen wechselt die Zuordnung">
                <span><Button size="small" disabled={busy} onClick={() => link(u.id)}>Umhängen</Button></span>
              </Tooltip>
            ) : (
              <Button size="small" disabled={busy} onClick={() => link(u.id)}>Verknüpfen</Button>
            )}
          </Stack>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};

const InvoiceCustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [membersFor, setMembersFor] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [actionMsg, setActionMsg] = useState(null);
  const [creatingId, setCreatingId] = useState(null);
  const [openingId, setOpeningId] = useState(null);
  const [sendingId, setSendingId] = useState(null);

  useEffect(() => {
    const q = query(collection(db, 'invoiceCustomers'), orderBy('customerNumber', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      setCustomers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => { setLoadError(err.message); setLoading(false); });
    return unsub;
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'invoices'), orderBy('issuedAt', 'desc'), limit(50));
    const unsub = onSnapshot(q, (snap) => {
      setInvoices(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, () => {});
    return unsub;
  }, []);

  const createInvoice = async (c) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Rechnung für ${c.displayName || c.companyName} aus ${c.openRideCount || 0} offenen Fahrten erstellen?`)) return;
    setCreatingId(c.id); setActionMsg(null);
    try {
      const res = await httpsCallable(functions, 'createInvoiceNow')({ invoiceCustomerId: c.id });
      setActionMsg({ severity: 'success', message: `Rechnung ${res.data?.invoiceNumber || ''} erstellt und versendet.` });
    } catch (e) {
      setActionMsg({ severity: 'error', message: e?.message || 'Rechnung konnte nicht erstellt werden.' });
    } finally { setCreatingId(null); }
  };

  const openInvoicePdf = async (inv) => {
    if (!inv.pdfPath) return;
    setOpeningId(inv.id);
    try {
      const url = await getDownloadURL(storageRef(storage, inv.pdfPath));
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      setActionMsg({ severity: 'error', message: 'PDF konnte nicht geladen werden.' });
    } finally { setOpeningId(null); }
  };

  const markPaid = async (inv) => {
    setActionMsg(null);
    try {
      await httpsCallable(functions, 'markInvoicePaid')({ invoiceId: inv.id });
    } catch (e) {
      setActionMsg({ severity: 'error', message: e?.message || 'Konnte nicht als bezahlt markiert werden.' });
    }
  };

  const invoiceStatusChip = (inv) => {
    const s = inv.status || 'issued';
    const map = { issued: ['erstellt', 'default'], sent: ['versendet', 'info'], paid: ['bezahlt', 'success'], overdue: ['überfällig', 'error'], cancelled: ['storniert', 'warning'] };
    const [label, color] = map[s] || [s, 'default'];
    return <Chip size="small" label={label} color={color} variant={color === 'default' ? 'outlined' : 'filled'} />;
  };

  /** Versandstatus der Rechnungs-E-Mail (billing.autoSendEmail pro Kunde). */
  const emailStatusChip = (inv) => {
    const s = inv.email?.status;
    const map = {
      manual: ['wartet auf Versand', 'warning'],
      queued: ['in Zustellung', 'info'],
      sent: ['E-Mail gesendet', 'success'],
      failed: ['Versand fehlgeschlagen', 'error'],
      no_address: ['keine E-Mail-Adresse', 'default'],
      not_configured: ['Versand nicht eingerichtet', 'default']
    };
    if (!map[s]) return null;
    const [label, color] = map[s];
    return <Chip size="small" label={label} color={color} variant="outlined" sx={{ mt: 0.5 }} />;
  };

  /** Rechnung manuell per E-Mail versenden (auch Nachversand nach Fehler). */
  const sendInvoice = async (inv) => {
    setSendingId(inv.id); setActionMsg(null);
    try {
      const res = await httpsCallable(functions, 'sendInvoiceEmail')({ invoiceId: inv.id });
      setActionMsg({ severity: 'success', message: `Rechnung ${inv.invoiceNumber} an ${res.data?.to} versendet.` });
    } catch (e) {
      setActionMsg({ severity: 'error', message: e?.message || 'Versand fehlgeschlagen.' });
    } finally { setSendingId(null); }
  };

  const openCreate = useCallback(() => { setForm(emptyForm()); setFormError(''); setFormOpen(true); }, []);
  const openEdit = useCallback((c) => {
    setForm({
      id: c.id, customerNumber: c.customerNumber || null,
      displayName: c.displayName || '', companyName: c.companyName || '',
      contactPerson: c.contactPerson || '', street: c.address?.street || '', zip: c.address?.zip || '',
      city: c.address?.city || '', country: c.address?.country || 'Deutschland', vatId: c.vatId || '',
      billingEmail: c.billing?.email || '', ccEmails: (c.billing?.ccEmails || []).join(', '),
      mode: c.billing?.mode || 'monthly', priceMode: c.billing?.priceMode || 'metered',
      paymentTermsDays: c.billing?.paymentTermsDays ?? 14, reference: c.billing?.reference || '',
      autoSendEmail: c.billing?.autoSendEmail === true,
      maxFarePerRideEur: c.limits?.maxFarePerRideEur ?? '', monthlyBudgetEur: c.limits?.monthlyBudgetEur ?? '',
      active: c.active !== false, notes: c.notes || ''
    });
    setFormError(''); setFormOpen(true);
  }, []);

  const setField = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const handleSave = async () => {
    setFormError(''); setSaving(true);
    try {
      await httpsCallable(functions, 'saveInvoiceCustomer')({
        id: form.id || undefined,
        displayName: form.displayName.trim() || form.companyName.trim(),
        companyName: form.companyName.trim(),
        contactPerson: form.contactPerson.trim(),
        address: { street: form.street.trim(), zip: form.zip.trim(), city: form.city.trim(), country: form.country.trim() },
        vatId: form.vatId.trim(),
        billing: {
          email: form.billingEmail.trim(),
          ccEmails: form.ccEmails.split(',').map((s) => s.trim()).filter(Boolean),
          mode: form.mode, priceMode: form.priceMode,
          paymentTermsDays: Number(form.paymentTermsDays) || 14,
          reference: form.reference.trim(),
          autoSendEmail: form.autoSendEmail
        },
        limits: {
          maxFarePerRideEur: form.maxFarePerRideEur === '' ? null : Number(form.maxFarePerRideEur),
          monthlyBudgetEur: form.monthlyBudgetEur === '' ? null : Number(form.monthlyBudgetEur)
        },
        active: form.active, notes: form.notes.trim()
      });
      setFormOpen(false);
    } catch (e) {
      setFormError(e?.message || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2, md: 3 } }}>
      <PageHeader
        title="Rechnungskunden"
        description="Firmen-/Geschäftskunden, die auf Rechnung fahren"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Kunden', path: '/kunden' },
          { label: 'Rechnungskunden', path: '/rechnungskunden' }
        ]}
        actionButton={{ label: 'Rechnungskunde anlegen', icon: <AddIcon />, onClick: openCreate }}
      />

      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
      {actionMsg && <Alert severity={actionMsg.severity} sx={{ mb: 2 }} onClose={() => setActionMsg(null)}>{actionMsg.message}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nr.</TableCell>
              <TableCell>Firma</TableCell>
              <TableCell>Ansprechpartner</TableCell>
              <TableCell align="right">Offene Fahrten</TableCell>
              <TableCell align="right">Offener Betrag</TableCell>
              <TableCell>Modus</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customers.map((c) => (
              <TableRow key={c.id} hover>
                <TableCell>{c.customerNumber || '—'}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{c.displayName || c.companyName}</Typography>
                  {c.companyName && c.displayName && c.companyName !== c.displayName && (
                    <Typography variant="caption" color="text.secondary">{c.companyName}</Typography>
                  )}
                </TableCell>
                <TableCell>{c.contactPerson || '—'}</TableCell>
                <TableCell align="right">{c.openRideCount || 0}</TableCell>
                <TableCell align="right">{fmtMoney(c.openAmountEur)}</TableCell>
                <TableCell>{c.billing?.mode === 'per_ride' ? 'Einzeln' : 'Monatlich'}</TableCell>
                <TableCell>
                  <Chip size="small" label={c.active === false ? 'inaktiv' : 'aktiv'} color={c.active === false ? 'default' : 'success'} variant={c.active === false ? 'outlined' : 'filled'} />
                  {/* Schnellanlage aus dem Fahrtformular (Verordnungspflicht-
                      Konzept §8.4): bis zur Nachpflege keine Rechnung möglich. */}
                  {c.billingProfileComplete === false && (
                    <Chip size="small" label="unvollständig" color="warning" variant="outlined" sx={{ ml: 0.5 }} />
                  )}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={c.billingProfileComplete === false
                    ? 'Rechnungskunde unvollständig — bitte Adresse und Rechnungs-E-Mail ergänzen'
                    : (c.openRideCount ? 'Rechnung jetzt erstellen' : 'Keine offenen Fahrten')}>
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        disabled={!c.openRideCount || creatingId === c.id || c.billingProfileComplete === false}
                        onClick={() => createInvoice(c)}
                      >
                        {creatingId === c.id ? <CircularProgress size={16} /> : <ReceiptIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Verknüpfte Kunden">
                    <IconButton size="small" onClick={() => setMembersFor(c)}>
                      <PeopleIcon fontSize="small" />
                      {c.memberCount ? <Typography variant="caption" sx={{ ml: 0.3 }}>{c.memberCount}</Typography> : null}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Bearbeiten">
                    <IconButton size="small" onClick={() => openEdit(c)}><EditIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {!loading && customers.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Box sx={{ py: 3, color: 'text.secondary' }}>
                    <BusinessIcon />
                    <Typography variant="body2">Noch keine Rechnungskunden — oben „Rechnungskunde anlegen".</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {loading && (
              <TableRow><TableCell colSpan={8} align="center"><CircularProgress size={22} sx={{ my: 2 }} /></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Rechnungsübersicht */}
      <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>Rechnungen</Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nr.</TableCell>
              <TableCell>Kunde</TableCell>
              <TableCell>Datum</TableCell>
              <TableCell>Zeitraum</TableCell>
              <TableCell align="right">Betrag</TableCell>
              <TableCell align="center">Fahrten</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.map((inv) => {
              const from = inv.period?.from?.toDate ? inv.period.from.toDate() : null;
              const to = inv.period?.to?.toDate ? inv.period.to.toDate() : null;
              const issued = inv.issuedAt?.toDate ? inv.issuedAt.toDate() : null;
              const fmtD = (d) => d ? d.toLocaleDateString('de-DE') : '—';
              return (
                <TableRow key={inv.id} hover>
                  <TableCell>{inv.invoiceNumber}</TableCell>
                  <TableCell>{inv.customer?.companyName || '—'}</TableCell>
                  <TableCell>{fmtD(issued)}</TableCell>
                  <TableCell>{from && to ? `${fmtD(from)}–${fmtD(to)}` : '—'}</TableCell>
                  <TableCell align="right">{fmtMoney(inv.totals?.grossEur)}</TableCell>
                  <TableCell align="center">{inv.lineCount || (inv.rideIds ? inv.rideIds.length : 0)}</TableCell>
                  <TableCell>
                    {invoiceStatusChip(inv)}
                    <br />
                    {emailStatusChip(inv)}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="PDF öffnen">
                      <span><IconButton size="small" disabled={openingId === inv.id} onClick={() => openInvoicePdf(inv)}><PdfIcon fontSize="small" /></IconButton></span>
                    </Tooltip>
                    {inv.status !== 'cancelled' && (
                      <Tooltip title={inv.email?.status === 'sent'
                        ? 'Erneut per E-Mail senden'
                        : inv.email?.status === 'failed'
                          ? 'Versand fehlgeschlagen — erneut senden'
                          : 'Per E-Mail an den Rechnungskunden senden'}>
                        <span>
                          <IconButton size="small" color="primary" disabled={sendingId === inv.id} onClick={() => sendInvoice(inv)}>
                            {sendingId === inv.id ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {inv.status !== 'paid' && inv.status !== 'cancelled' && (
                      <Tooltip title="Als bezahlt markieren">
                        <IconButton size="small" color="success" onClick={() => markPaid(inv)}><PaidIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {invoices.length === 0 && (
              <TableRow><TableCell colSpan={8} align="center"><Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>Noch keine Rechnungen erstellt.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Anlegen / Bearbeiten — Pflichtangaben nach § 14 UStG:
          Firmenname + Anschrift (Leistungsempfänger) + Versandziel. */}
      <Dialog open={formOpen} onClose={() => !saving && setFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <BusinessIcon color="primary" />
            <Box sx={{ flexGrow: 1 }}>
              {form.id ? 'Rechnungskunde bearbeiten' : 'Rechnungskunde anlegen'}
              <Typography variant="body2" color="text.secondary">
                {form.id
                  ? 'Stammdaten und Abrechnungseinstellungen ändern'
                  : 'Die Kundennummer wird beim Anlegen automatisch vergeben.'}
              </Typography>
            </Box>
            {form.id && form.customerNumber && (
              <Chip size="small" variant="outlined" label={`Kd.-Nr. ${form.customerNumber}`} />
            )}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Grid container spacing={2}>
            <FormSection first icon={<BusinessIcon fontSize="small" color="action" />} title="Firma & Kontakt" />
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth required size="small" label="Firmenname (Rechnungsempfänger)"
                value={form.companyName} onChange={setField('companyName')}
                helperText="Erscheint als Leistungsempfänger auf der Rechnung" />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Anzeigename (optional)" value={form.displayName}
                onChange={setField('displayName')} helperText="Kurzname für Listen — leer = Firmenname" />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Ansprechpartner" value={form.contactPerson} onChange={setField('contactPerson')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="USt-IdNr (optional)" placeholder="DE123456789"
                value={form.vatId} onChange={setField('vatId')}
                helperText="Nur bei EU-Auslandsbezug nötig" />
            </Grid>

            <FormSection icon={<AddressIcon fontSize="small" color="action" />} title="Rechnungsanschrift" hint="Pflicht nach § 14 UStG" />
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth required size="small" label="Straße und Hausnummer" value={form.street} onChange={setField('street')} />
            </Grid>
            <Grid size={{ xs: 4, sm: 2 }}>
              <TextField fullWidth required size="small" label="PLZ" value={form.zip} onChange={setField('zip')} />
            </Grid>
            <Grid size={{ xs: 8, sm: 4 }}>
              <TextField fullWidth required size="small" label="Ort" value={form.city} onChange={setField('city')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Land" value={form.country} onChange={setField('country')} />
            </Grid>

            <FormSection icon={<MailIcon fontSize="small" color="action" />} title="Rechnungsversand" />
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth required size="small" type="email" label="Rechnungs-E-Mail"
                value={form.billingEmail} onChange={setField('billingEmail')}
                error={Boolean(form.billingEmail.trim()) && !EMAIL_REGEX.test(form.billingEmail.trim())}
                helperText="An diese Adresse werden die Rechnungs-PDFs gesendet" />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Kopie-Empfänger (CC)" value={form.ccEmails}
                onChange={setField('ccEmails')} helperText="Mehrere Adressen mit Komma trennen" />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.autoSendEmail}
                    onChange={(e) => setForm((p) => ({ ...p, autoSendEmail: e.target.checked }))}
                  />
                }
                label={form.autoSendEmail
                  ? 'Rechnungen automatisch per E-Mail versenden'
                  : 'Rechnungen erst kontrollieren, dann manuell versenden (empfohlen für den Start)'}
              />
              <Typography variant="caption" color="text.secondary" component="div" sx={{ ml: 6, mt: -0.5 }}>
                Aus = die Rechnung wird erstellt und wartet in der
                Rechnungsübersicht auf den Versand per Knopfdruck.
              </Typography>
            </Grid>

            <FormSection icon={<BillingIcon fontSize="small" color="action" />} title="Abrechnung" />
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Abrechnung</InputLabel>
                <Select label="Abrechnung" value={form.mode} onChange={setField('mode')}>
                  <MenuItem value="monthly">Monatliche Sammelrechnung</MenuItem>
                  <MenuItem value="per_ride">Einzelrechnung je Fahrt</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Preisbasis</InputLabel>
                <Select label="Preisbasis" value={form.priceMode} onChange={setField('priceMode')}>
                  <MenuItem value="metered">Taxameter</MenuItem>
                  <MenuItem value="fixed">Festpreis</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" type="number" label="Zahlungsziel (Tage)"
                value={form.paymentTermsDays} onChange={setField('paymentTermsDays')}
                InputProps={{ inputProps: { min: 0, max: 90 } }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Kundenreferenz / Kostenstelle" value={form.reference}
                onChange={setField('reference')} helperText="Erscheint als „Ihre Referenz“ auf der Rechnung" />
            </Grid>

            <FormSection icon={<LimitsIcon fontSize="small" color="action" />} title="Limits & Status" hint="optional — weiche Warnungen, keine harte Sperre" />
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField fullWidth size="small" type="number" label="Limit je Fahrt"
                value={form.maxFarePerRideEur} onChange={setField('maxFarePerRideEur')}
                InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment>, inputProps: { min: 0 } }} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField fullWidth size="small" type="number" label="Monatsbudget"
                value={form.monthlyBudgetEur} onChange={setField('monthlyBudgetEur')}
                InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment>, inputProps: { min: 0 } }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={<Switch checked={form.active} onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))} />}
                label={form.active ? 'Aktiv — neue Rechnungsfahrten möglich' : 'Inaktiv — keine neuen Rechnungsfahrten'}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth size="small" multiline minRows={2} label="Notizen (intern)" value={form.notes} onChange={setField('notes')} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)} disabled={saving}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSave}
            disabled={
              saving ||
              !form.companyName.trim() || !form.street.trim() || !form.zip.trim() || !form.city.trim() ||
              !EMAIL_REGEX.test(form.billingEmail.trim())
            }>
            {saving ? 'Speichert…' : form.id ? 'Änderungen speichern' : 'Rechnungskunde anlegen'}
          </Button>
        </DialogActions>
      </Dialog>

      {membersFor && <MembersDialog invoiceCustomer={membersFor} onClose={() => setMembersFor(null)} />}
    </Box>
  );
};

export default InvoiceCustomersPage;
